import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAnalytics } from '../../../../../ui-kitchen/src/components/AnalyticsProvider';
import { selectExperimentsReady } from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import {
  configureGam,
  getAdContext,
  getAdDimension,
  getGamLazyLoadOptions,
  getAdPlacementId,
  getRequestPage,
  shouldDisplayAd,
} from '../../../../../ui-kitchen/src/components/AmexAds/helpers';
import {
  ADS_ODF_CALL_SWITCH_TO_BACKEND,
  ADS_WEB_PLACEMENT_HOMEPAGE_ENABLED,
  ADS_WEB_PLACEMENT_USER_MENU_ENABLED,
} from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';
import { fetchAmexAdsSegmentThunk } from '../../../../../ui-kitchen/src/reducers/amexAdsSlice';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { selectResyAdsId } from '../../../../../ui-kitchen/src/reducers/userSlice';

const AD_EVENT_SOURCE = 'web';
const RESIZE_DEBOUNCE_MS = 150;
const ODF_RESPONSE_EMPTY = 'empty';
const ODF_RESPONSE_SEGMENT_INFO_RETURNED = 'segment_info_returned';
const ODF_RESPONSE_CALL_ERROR = 'odf_call_error';

const getOdfResponseStatus = ({ payload, error }) => {
  if (error) return ODF_RESPONSE_CALL_ERROR;
  if (!payload || Object.keys(payload).length === 0) return ODF_RESPONSE_EMPTY;
  return ODF_RESPONSE_SEGMENT_INFO_RETURNED;
};

const dispatchAdEvent = (callbacks, event) => {
  if (!event || !event.type) return;
  switch (event.type) {
    case 'gam_call':
      callbacks.onGamCall?.({ odfResponse: event.odfResponse });
      break;
    case 'empty':
      callbacks.onAdEmpty?.();
      break;
    case 'rendered':
      callbacks.onAdRendered?.();
      break;
    case 'load_success':
      callbacks.onAdLoadSuccess?.({ adId: event.adId });
      break;
    case 'load_timeout':
      callbacks.onAdLoadTimeout?.();
      break;
    case 'impression':
      callbacks.onAdImpression?.({ adId: event.adId });
      break;
    case 'click':
      callbacks.onAdClick?.({ adId: event.adId });
      break;
    default:
  }
};

const useDebouncedViewportWidth = (debounceMs = RESIZE_DEBOUNCE_MS) => {
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setViewportWidth(window.innerWidth), debounceMs);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [debounceMs]);

  return viewportWidth;
};

const useGTagLoaded = () => {
  const [gTagLoaded, setGTagLoaded] = useState(() => !!window.googletag?.apiReady);

  useEffect(() => {
    if (window.googletag?.apiReady) return undefined;
    window.googletag = window.googletag || { cmd: [] };
    let cancelled = false;
    window.googletag.cmd.push(() => {
      if (!cancelled) setGTagLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  return gTagLoaded;
};

const useAdKeyForSize = (width, height) => {
  const [adKey, setAdKey] = useState(0);
  const lastSizeRef = useRef({ width, height });
  useEffect(() => {
    if (lastSizeRef.current.width !== width || lastSizeRef.current.height !== height) {
      lastSizeRef.current = { width, height };
      setAdKey((k) => k + 1);
    }
  }, [width, height]);
  return adKey;
};

const useAdAnalyticsHandlers = ({ Analytics, page, routeKey }) => {
  const adLoadEventFiredRef = useRef(false);
  const gamCallEventFiredRef = useRef(false);
  const impressedAdIdsRef = useRef(new Set());

  useEffect(() => {
    adLoadEventFiredRef.current = false;
    gamCallEventFiredRef.current = false;
    impressedAdIdsRef.current = new Set();
  }, [routeKey]);

  const adContext = getAdContext(page);

  const handleAdLoadSuccess = useCallback(({ adId } = {}) => {
    if (adLoadEventFiredRef.current) return;
    adLoadEventFiredRef.current = true;
    Analytics?.event('ad_load', {
      value: 'successful',
      source: AD_EVENT_SOURCE,
      context: adContext,
      ad_id: adId,
    });
  }, [Analytics, adContext]);

  const handleAdLoadTimeout = useCallback(() => {
    if (adLoadEventFiredRef.current) return;
    adLoadEventFiredRef.current = true;
    Analytics?.event('ad_load', {
      value: 'timeout',
      source: AD_EVENT_SOURCE,
      context: adContext,
    });
  }, [Analytics, adContext]);

  const handleAdImpression = useCallback(({ adId } = {}) => {
    if (adId && impressedAdIdsRef.current.has(adId)) return;
    if (adId) impressedAdIdsRef.current.add(adId);
    Analytics?.event('ad_impression', {
      value: 1,
      source: AD_EVENT_SOURCE,
      context: adContext,
      ad_id: adId,
    });
  }, [Analytics, adContext]);

  const handleAdClick = useCallback(({ adId } = {}) => {
    Analytics?.event('ad_click', {
      value: 1,
      source: AD_EVENT_SOURCE,
      context: adContext,
      ad_id: adId,
    });
  }, [Analytics, adContext]);

  const handleGamCall = useCallback(({ odfResponse }) => {
    if (gamCallEventFiredRef.current) return;
    gamCallEventFiredRef.current = true;
    const eventPayload = {
      source: AD_EVENT_SOURCE,
      context: adContext,
      odf_response: odfResponse,
    };
    Analytics?.event('ad_gam_call', eventPayload);
  }, [Analytics, adContext]);

  const handleOdfCall = useCallback(({ requestPage }) => {
    Analytics?.event('odf_call', {
      source: AD_EVENT_SOURCE,
      context: adContext,
      request_page: requestPage,
    });
  }, [Analytics, adContext]);

  const handleAdPlacementFlags = useCallback(({
    homepageEnabled,
    userMenuEnabled,
  }) => {
    Analytics?.event('ad_placement_flags', {
      source: AD_EVENT_SOURCE,
      homepage_enabled: homepageEnabled,
      user_menu_enabled: userMenuEnabled,
    });
  }, [Analytics]);

  return {
    handleAdLoadSuccess,
    handleAdLoadTimeout,
    handleAdImpression,
    handleAdClick,
    handleGamCall,
    handleOdfCall,
    handleAdPlacementFlags,
  };
};

const useGamSlot = ({
  enabled, placementId, page, payload, odfResponse, adKey, callbacks, gamOptions,
}) => {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!enabled) return undefined;
    const cleanup = configureGam({
      placementId,
      page,
      divId: placementId,
      payload,
      odfResponse,
      gamOptions,
      onAdEvent: (event) => dispatchAdEvent(callbacksRef.current, event),
    });
    return cleanup;
  }, [enabled, adKey]); // eslint-disable-line
};

// Ads are rendered inside an iframe. Focus events are not propagated from the iframe to the parent document,
// so we need to listen for focus events on the iframe's document. The useFocusTracker hook sets up event listeners
// for focusin and focusout events on the iframe's document and updates the isFocused state accordingly.
// It also cleans up the event listeners when the component unmounts or when the ad is no longer displaying.
// The isAdFocused state is then used to conditionally apply a CSS class to the ad slot, which can be used to style
// the ad differently when it is focused. The useFocusTracker hook takes a ref to the ad container and a boolean
// indicating whether the ad is currently displaying. It returns a boolean indicating whether the ad is currently focused.
const useFocusTracker = (ref, isAdDisplaying) => {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const slot = ref.current;
    if (!slot || !isAdDisplaying) return undefined;

    const handleFocusIn = () => setIsFocused(true);
    const handleFocusOut = () => setIsFocused(false);

    const iframe = document.querySelector('div.amex-ads-container iframe');
    if (iframe) {
      iframe.contentDocument?.addEventListener('focusin', handleFocusIn);
      iframe.contentDocument?.addEventListener('focusout', handleFocusOut);
    }

    return () => {
      if (iframe) {
        iframe.contentDocument?.removeEventListener('focusin', handleFocusIn);
        iframe.contentDocument?.removeEventListener('focusout', handleFocusOut);
      }
    };
  }, [ref, isAdDisplaying]);

  return isFocused;
};

const useAmexAdsContainerState = ({ page, routeKey }) => {
  const dispatch = useDispatch();
  const Analytics = useAnalytics();

  const viewportWidth = useDebouncedViewportWidth();

  const isLoggedIn = useSelector(selectIsAuthenticated);
  const isExperimentsReady = useSelector(selectExperimentsReady);
  const payload = useSelector((state) => state.amexAds?.payload);
  const isAdLoading = useSelector((state) => state.amexAds?.isLoading);
  const adSegmentError = useSelector((state) => state.amexAds?.error);
  const encryptedResyAdsId = useSelector(selectResyAdsId);
  const userId = useSelector((state) => state.user?.userData?.id);
  const experiments = useSelector((state) => state.experiments);
  const countryCode = useSelector((state) => state.ipdata?.countryCode);
  const placementHomepageEnabled = !!experiments?.[ADS_WEB_PLACEMENT_HOMEPAGE_ENABLED]?.enabled;
  const placementUserMenuEnabled = !!experiments?.[ADS_WEB_PLACEMENT_USER_MENU_ENABLED]?.enabled;
  const useBackendApi = !!experiments?.[ADS_ODF_CALL_SWITCH_TO_BACKEND]?.enabled;

  const { height, width } = getAdDimension(page, viewportWidth);
  const placementId = getAdPlacementId(page);
  const isAdEnabled = shouldDisplayAd(experiments, page, countryCode);
  const adKey = useAdKeyForSize(width, height);
  const gTagLoaded = useGTagLoaded();

  const containerRef = useRef(null);

  const [isAdEmpty, setIsAdEmpty] = useState(false);
  const [isAdDisplaying, setIsAdDisplaying] = useState(false);
  const lastPlacementFlagsUserIdRef = useRef(null);

  const {
    handleAdLoadSuccess,
    handleAdLoadTimeout,
    handleAdImpression,
    handleAdClick,
    handleGamCall,
    handleOdfCall,
    handleAdPlacementFlags,
  } = useAdAnalyticsHandlers({ Analytics, page, routeKey });

  useEffect(() => {
    if (!isLoggedIn) {
      lastPlacementFlagsUserIdRef.current = null;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !isExperimentsReady || !userId) return;
    if (lastPlacementFlagsUserIdRef.current === userId) return;
    handleAdPlacementFlags({
      homepageEnabled: placementHomepageEnabled,
      userMenuEnabled: placementUserMenuEnabled,
    });
    lastPlacementFlagsUserIdRef.current = userId;
  }, [
    isLoggedIn,
    isExperimentsReady,
    userId,
    placementHomepageEnabled,
    placementUserMenuEnabled,
    handleAdPlacementFlags,
  ]);

  useEffect(() => {
    if (!isExperimentsReady || !isAdEnabled || !isLoggedIn) return;

    if (!useBackendApi && !encryptedResyAdsId) return;

    const requestPage = getRequestPage(page);
    handleOdfCall({ requestPage });
    dispatch(fetchAmexAdsSegmentThunk({
      requestPage,
      useBackendApi,
      encryptedResyAdsId,
    }));
  }, [dispatch, isExperimentsReady, isAdEnabled, isLoggedIn, useBackendApi, encryptedResyAdsId, page, handleOdfCall]);

  const isVisible = isExperimentsReady && isLoggedIn && isAdEnabled && !isAdEmpty;
  const isAdReady = !isAdLoading;
  const isAdFocused = useFocusTracker(containerRef, isAdDisplaying);
  const gamOptions = getGamLazyLoadOptions(experiments);
  const odfResponse = getOdfResponseStatus({ payload, error: adSegmentError });

  useGamSlot({
    enabled: isVisible && isAdReady,
    placementId,
    page,
    payload,
    odfResponse,
    adKey,
    gamOptions,
    callbacks: {
      onGamCall: handleGamCall,
      onAdEmpty: () => setIsAdEmpty(true),
      onAdRendered: () => setIsAdDisplaying(true),
      onAdLoadSuccess: handleAdLoadSuccess,
      onAdLoadTimeout: handleAdLoadTimeout,
      onAdImpression: handleAdImpression,
      onAdClick: handleAdClick,
    },
  });

  return {
    containerRef,
    height,
    width,
    placementId,
    adKey,
    isVisible,
    isAdReady,
    isAdDisplaying,
    gTagLoaded,
    isAdFocused,
  };
};

export default useAmexAdsContainerState;
